/**
 * 📌 Script: main.js
 * 📂 Caminho: src/main.js
 * 📝 Descrição: Boot da aplicação. Cria Vue app, registra Pinia,
 *   Router e I18n. Aguarda leitura do idioma no Firestore antes
 *   de montar a UI. Injeta o manifesto PWA correto por URL e
 *   registra o service worker.
 *
 * 🔗 Dependências:
 *  - pinia, vue-router, vue-i18n
 *  - Firebase usado indiretamente por i18nBoot.js
 */

import { createApp } from "vue";
import App from "./App.vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import router from "./router";
import "./assets/styles/tailwind.css";
import { createI18n } from "vue-i18n";

// 🔗 Injeta o manifesto PWA correto conforme a URL de entrada
function injectPwaManifest() {
  const path = window.location.pathname;
  let href = "/site.webmanifest";
  if (path.startsWith("/app/zeiterfassung")) {
    href = "/zeit.webmanifest";
  } else if (path.startsWith("/app")) {
    href = "/sps.webmanifest";
  }
  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = href;
  document.head.appendChild(link);
}
injectPwaManifest();

// 🔧 Registra Service Worker (PWA offline/cache de assets)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[SW] Falha ao registrar service worker:", err);
    });
  });
}

// 📚 Descoberta automática dos JSONs de idioma (fallback garante 'de')
const ctx = require.context("./locales", false, /^\.\/[a-z]{2}\.json$/);
const messages = ctx.keys().reduce((acc, p) => {
  const m = p.match(/\.\/([a-z]{2})\.json$/i);
  const code = m ? m[1].toLowerCase() : null;
  if (!code) return acc;
  const mod = ctx(p);
  acc[code] = mod.default || mod;
  return acc;
}, {});
if (!messages.de) messages.de = {};

// 🔧 Registro específico da Vitrine (merge não intrusivo)
import { detectInitialLocale, registerVitrineMessages } from "./i18nVitrine";

// 🔧 Boot de i18n: aplica idioma do usuário via Firestore
import { initI18nBoot } from "./i18nBoot";

// ⚠️ i18n inicia em 'de' e injeta globalmente ($t disponível em todo app)
const i18n = createI18n({
  legacy: false, // API Composition do vue-i18n
  globalInjection: true, // permite usar $t sem importar useI18n
  locale: "de", // inicia neutro; i18nBoot ajusta depois
  fallbackLocale: "de", // SEMPRE alemão como fallback
  messages,
});

// ⏳ Aguarda i18nBoot para evitar "flash" no idioma
(async () => {
  // registra chaves da vitrine (merge não intrusivo)
  registerVitrineMessages(i18n);

  // se não houver cache, usa idioma do navegador (fallback 'de')
  try {
    const cached = localStorage.getItem("sps.lang");
    if (!cached) {
      i18n.global.locale.value = detectInitialLocale();
    }
  } catch (e) {
    i18n.global.locale.value = detectInitialLocale();
  }

  await initI18nBoot(i18n);

  const app = createApp(App);

  // 🔌 Plugins da aplicação
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);
  app.use(pinia); // estado global
  app.use(router); // rotas
  app.use(i18n); // i18n já com locale aplicado

  app.mount("#app"); // 🚀 monta a UI após i18n pronto

  // 📲 Inicializa captura do beforeinstallprompt para PWA
  const { usePwaInstallStore } = await import("./stores/pwaInstallStore");
  usePwaInstallStore().init();
})();
